/**
 * Who a customer quotation is emailed to.
 *
 * ---- why the frontend resolves this at all ----
 *
 * `send_quotation` takes an optional `to`, and falls back to looking the address up itself:
 *
 *     # apps/quoflow/.../api/quotation.py:63
 *     customer_email = to or [frappe.db.get_value("Customer", doc.customer, "customer_primary_contact")]
 *
 * That fallback has two bugs (ask `B8`, measured 2026-08-09). `customer_primary_contact` is not a
 * column on this bench — the field is `primary_contact` — so it raises
 * `OperationalError (1054) Unknown column`. And `primary_contact` is a **Link to `Contact`**, so
 * even corrected it would return a DOCNAME rather than an address and try to mail
 * `"Akhil Nair-Prantik"`.
 *
 * We used to send `to: []`. **An empty list is falsy in Python**, so every send fell into that
 * branch — which is why sending and re-issuing both died on the same SQL error. Passing a real
 * address avoids the branch entirely, and that is what this module produces.
 *
 * ---- where the addresses actually live ----
 *
 * Not on `Customer`: `primary_contact` is set on **0 of 1561** of them. They live on `Contact`,
 * linked the standard ERPNext way through the `Dynamic Link` child table — 4,039 of 4,221
 * contacts carry an `email_id`. One query finds them, filtering the parent by its child:
 *
 *     /api/resource/Contact?filters=[["Dynamic Link","link_name","=","<customer>"],
 *                                    ["Dynamic Link","link_doctype","=","Customer"]]
 *
 * This is the same lookup `items.get_supplier_emails` performs for suppliers.
 *
 * ---- two live quirks the pure function handles ----
 *
 * 1. **Duplicate rows.** Filtering a parent by a child table returns one row PER MATCHING LINK,
 *    so a contact linked twice comes back twice — measured on `3 STAR MARINE SERVICES`.
 * 2. **Leading spaces.** Addresses come back as `" accounts@sunshineagency.ae"`, the same defect
 *    `wire.ts`'s `trimmed()` handles for `supplier_name`. Untrimmed, they are not valid
 *    recipients.
 */

import { useQuery } from '@tanstack/react-query'
import { getResource, getResourceDoc } from './frappe'
import { num, str } from './wire'

/** One `Contact` row as the filtered `/api/resource/Contact` query returns it. */
export interface WireContact {
  name: string
  email_id?: string | null
  is_primary_contact?: number | boolean | null
}

/**
 * The addresses to put in `to`, primary first — deduplicated, trimmed, and never containing an
 * empty string.
 *
 * An empty result means "we cannot reach this customer", which the caller MUST act on rather
 * than send anyway: an empty `to` is precisely what drops the server into the broken fallback.
 */
export function pickRecipients(rows: readonly WireContact[]): string[] {
  const seen = new Set<string>()
  const out: { email: string; primary: boolean }[] = []

  for (const row of rows) {
    const email = str(row.email_id).trim()
    if (email === '') continue
    const key = email.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push({
      email,
      primary: num(row.is_primary_contact) === 1 || row.is_primary_contact === true,
    })
  }

  // Stable: primary first, everything else in the order the server gave it.
  return [...out.filter((r) => r.primary), ...out.filter((r) => !r.primary)].map((r) => r.email)
}

/**
 * The `Customer` document's OWN address, folded in beside its contacts.
 *
 * ---- Why a Contact-only lookup was not enough ----
 *
 * `Customer.default_email` is a real field, populated from `primary_contact`, and a customer can
 * carry one with NO linked `Contact` whatsoever. Measured 2026-09-05: `shiby` has
 * `default_email: shibyavu@gmail.com` and zero rows in the `Dynamic Link` join, so the quotation
 * screen reported "No email address on file for this customer" over an address sitting on the
 * record in plain view.
 *
 * It is also the field the SERVER resolves the other side from — `items.get_supplier_emails`
 * answers off `Supplier.default_email` — so reading it here makes both halves of the app agree
 * about where an address lives.
 *
 * FIRST in the list, because it is the address the record itself nominates. De-duplicated
 * case-insensitively so a customer whose contact and default are the same address is not offered
 * it twice, and trimmed for the leading-space defect this wire carries throughout
 * (' accounts@sunshineagency.ae' observed live).
 */
export function mergeDefaultEmail(fromContacts: readonly string[], defaultEmail: unknown): string[] {
  const email = str(defaultEmail).trim()
  if (email === '') return [...fromContacts]
  const already = fromContacts.some((e) => e.trim().toLowerCase() === email.toLowerCase())
  return already ? [...fromContacts] : [email, ...fromContacts]
}

const CONTACT_STALE_MS = 5 * 60 * 1000

/**
 * The customer's reachable email addresses. `[]` is a real answer — 1561 customers on this bench
 * have no contact at all — and callers show it rather than sending into the void.
 */
export function useCustomerRecipients(customer: string | null | undefined) {
  return useQuery({
    queryKey: ['customer-recipients', customer ?? ''] as const,
    queryFn: async () => {
      const page = await getResource<WireContact>('Contact', {
        filters: [
          ['Dynamic Link', 'link_name', '=', customer ?? ''],
          ['Dynamic Link', 'link_doctype', '=', 'Customer'],
        ],
        fields: ['name', 'email_id', 'is_primary_contact'],
        limit: 20,
      })
      /*
        The customer's own `default_email` too — see `mergeDefaultEmail`. Read as a SEPARATE
        document fetch because the Contact query above is a join through `Dynamic Link` and cannot
        also name fields on `Customer`. A failure here is swallowed: the contacts are still a real
        answer, and losing them because the customer document could not be read would be strictly
        worse than missing one address.
      */
      let defaultEmail: unknown = ''
      try {
        const doc = await getResourceDoc<Record<string, unknown>>('Customer', customer ?? '')
        defaultEmail = doc?.default_email
      } catch {
        defaultEmail = ''
      }
      return mergeDefaultEmail(pickRecipients(page.data), defaultEmail)
    },
    enabled: !!customer,
    staleTime: CONTACT_STALE_MS,
  })
}
